import React, { useRef, useState } from 'react';
import useTranslate from '../../../../../customHooks/useTranslate';
import { useZitoneoSettings } from '../../../contexts/ZitoneoContentContext';
import URLItem from './URLItem';
import Button from '../../../../../muiComponents/Button';
import IconButton from '../../../../../muiComponents/IconButton';
import SuggestedURLItem from './SuggestedURLItem';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import {
    isAlreadyUsedPath,
    toURLSlug,
} from '../../../utils/zitoneoServicesUtils';
import Card from '../../../../../muiComponents/Card';

const URLSection = () => {
    const translate = useTranslate();
    const { URLs, addUrl, suggestedURLs, usedURLPaths } = useZitoneoSettings();

    const [isSuggestedSectionVisible, setIsSuggestedSectionVisible] =
        useState(false);
    const [isManuallySectionVisible, setIsManuallySectionVisible] =
        useState(false);
    const manualSectionRef = useRef<HTMLDivElement>(null);
    const suggestedSectionRef = useRef<HTMLDivElement>(null);
    const [manualUrl, setManualUrl] = useState('');
    const [manualUrlFeedback, setManualUrlFeedback] = useState('');

    const handleManualUrlChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setManualUrl(event.target.value);
        setManualUrlFeedback('');
    };

    const handleAddURL = () => {
        const url = manualUrl.trim();
        const normalizedUrl = toURLSlug(url);
        if (!normalizedUrl) {
            setManualUrl('');
            return;
        }
        if (
            !isAlreadyUsedPath(
                normalizedUrl,
                usedURLPaths,
                URLs.map(u => u.Path)
            )
        ) {
            addUrl(normalizedUrl);
            setManualUrl('');
        } else if (
            isAlreadyUsedPath(
                normalizedUrl,
                usedURLPaths,
                URLs.map(u => u.Path)
            )
        ) {
            setManualUrlFeedback('url_not_available');
        }
    };

    return (
        <div className='hotel-content-settings__section'>
            <div className='settings-section__header'>
                <h5 className='settings-section__title'>
                    {translate('urls_for_this_hotel')}
                </h5>
                <p className='settings-section__description'>
                    {translate('hotel_urls_description')}
                </p>
            </div>
            <div className='settings__urls-list'>
                {URLs.length ? (
                    URLs.map(url => <URLItem key={url.Id} url={url} />)
                ) : (
                    <Card className='settings__urls-list__item'>
                        <p>{translate('add_your_first_url')}</p>
                    </Card>
                )}
            </div>
            <div className='flex flex-end'>
                <Button
                    variant='contained'
                    onClick={() => {
                        setIsSuggestedSectionVisible(true);
                        setTimeout(
                            () =>
                                suggestedSectionRef.current?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                }),
                            0
                        );
                    }}
                >
                    {translate(
                        URLs.length
                            ? 'add_another_url'
                            : 'click_button_add_your_first_url'
                    )}
                </Button>
            </div>
            {isSuggestedSectionVisible && (
                <>
                    <div
                        ref={suggestedSectionRef}
                        className='settings-section__header'
                    >
                        <h5 className='settings-section__title'>
                            {translate('new_suggested_urls')}
                        </h5>
                        <IconButton
                            iconName='Close'
                            onClick={() => {
                                setIsSuggestedSectionVisible(false);
                                setIsManuallySectionVisible(false);
                            }}
                        />
                    </div>
                    <div className='settings__urls-list'>
                        {suggestedURLs.length ? (
                            suggestedURLs.map(url => (
                                <SuggestedURLItem key={url.path} url={url} />
                            ))
                        ) : (
                            <Card className='settings__urls-list__item'>
                                <p>{translate('no_suggested_urls')}</p>
                                <Button
                                    variant='text'
                                    startIcon='Edit'
                                    onClick={() => {
                                        setIsManuallySectionVisible(true);
                                        setTimeout(
                                            () =>
                                                manualSectionRef.current?.scrollIntoView(
                                                    {
                                                        behavior: 'smooth',
                                                        block: 'start',
                                                    }
                                                ),
                                            0
                                        );
                                    }}
                                >
                                    {translate('write_manually')}
                                </Button>
                            </Card>
                        )}
                    </div>
                    <div
                        ref={manualSectionRef}
                        className='gap-100 settings-section__header '
                    >
                        <h5 className='settings-section__title white-space-nowrap'>
                            {translate('write_manually')}
                        </h5>
                        <div className='flex flex-align-baseline gap-5'>
                            {isManuallySectionVisible && (
                                <p className='settings-section__description'>
                                    {translate('url_manual_description')}
                                </p>
                            )}
                            <IconButton
                                iconName={
                                    isManuallySectionVisible
                                        ? 'ExpandMore'
                                        : 'ChevronRight'
                                }
                                onClick={() =>
                                    setIsManuallySectionVisible(
                                        !isManuallySectionVisible
                                    )
                                }
                            />
                        </div>
                    </div>
                    {isManuallySectionVisible && (
                        <>
                            <div className='flex flex-align-start gap-5'>
                                <span
                                    className='settings__urls-item__separator flex flex-align-center'
                                    style={{ height: '38px' }}
                                >
                                    /
                                </span>
                                <TextInput
                                    value={manualUrl}
                                    onChange={handleManualUrlChange}
                                    deleteIcon={false}
                                    errorMessage={
                                        manualUrlFeedback
                                            ? translate(manualUrlFeedback)
                                            : ''
                                    }
                                />
                                <Button
                                    variant='contained'
                                    onClick={handleAddURL}
                                    className='white-space-nowrap height100perc'
                                    style={{ maxHeight: '38px' }}
                                >
                                    {translate('add_url')}
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default URLSection;
